import React, { useState, useMemo } from 'react';
import { 
  Modal, Form, Input, Select, Button, 
  message, Space, Typography, Divider, ConfigProvider 
} from 'antd';
import { 
  SendOutlined, 
  MessageFilled,
  InfoCircleOutlined,
  QuestionCircleOutlined,
  MessageOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../../api/axiosInstance';

const { TextArea } = Input;
const { Title, Text } = Typography;

const SupportChat = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const ticketTypeOptions = useMemo(
    () => [
      { value: 'Şikayet', label: t('supportChat.complaint') },
      { value: 'Öneri', label: t('supportChat.suggestion') },
      { value: 'Teknik', label: t('supportChat.technical') },
      { value: 'Sistem', label: t('supportChat.system') },
    ],
    [t]
  );

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      await axiosInstance.post('Tickets/Add', {
        type: values.type,
        message: values.description,
      });
      
      message.success({
        content: t('supportChat.success'),
        style: { marginTop: '20px' },
        duration: 3
      });

      form.resetFields();
      setIsModalOpen(false);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: '#FFCC29',
          borderRadius: 16,
        },
      }}
    >
      <>
        <div style={{ position: 'fixed', right: 30, bottom: 30, zIndex: 1000 }}>
          <motion.div
            onClick={() => setIsModalOpen(true)}
            whileHover={{ 
              scale: 1.15,
              rotate: [0, -7, 7, -7, 0],
            }}
            whileTap={{ scale: 0.9 }}
            transition={{ type: "spring", stiffness: 400, damping: 12 }}
            style={{ cursor: 'pointer', position: 'relative' }}
          >
            <motion.div
              animate={{
                scale: [1, 1.4, 1],
                opacity: [0.4, 0, 0.4],
              }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
              style={{
                position: 'absolute',
                width: '60px',
                height: '60px',
                backgroundColor: '#FFCC29',
                borderRadius: '50%',
                filter: 'blur(15px)',
                zIndex: -1
              }}
            />

            <motion.div
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              style={{
                width: '64px',
                height: '64px',
                backgroundColor: '#FFCC29',
                borderRadius: '22px', 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 12px 24px rgba(255, 204, 41, 0.4)',
                border: '2px solid #fff',
              }}
            >
              <motion.div
                animate={{ scale: [1, 1.2, 1] }}
                transition={{ duration: 0.6, repeat: Infinity, repeatDelay: 3, ease: "easeInOut" }}
              >
                <MessageFilled style={{ fontSize: '30px', color: '#000' }} />
              </motion.div>
            </motion.div>
          </motion.div>
        </div>

        <Modal
          title={
            <Space size="middle" align="center" style={{ padding: '4px 0' }}>
              <div style={{ 
                width: 42, height: 42, backgroundColor: '#FFFBE6', 
                borderRadius: '12px', display: 'flex', alignItems: 'center', 
                justifyContent: 'center', border: '1px solid #FFE58F', flexShrink: 0
              }}>
                <MessageOutlined style={{ color: '#FFCC29', fontSize: '22px' }} />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Title level={4} style={{ margin: 0, fontSize: '18px', fontWeight: 800, lineHeight: '1' }}>
                  {t('supportChat.title')}
                </Title>
                <Text type="secondary" style={{ fontSize: '12px', marginTop: '4px' }}>
                  {t('supportChat.subtitle')}
                </Text>
              </div>
            </Space>
          }
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          footer={null}
          centered
          width={450}
          destroyOnClose
          styles={{
            mask: { backdropFilter: 'blur(4px)' },
            content: { borderRadius: '24px', padding: '24px' }
          }}
        >
          <Divider style={{ margin: '12px 0 24px 0' }} />

          <Form form={form} layout="vertical" onFinish={handleSubmit} requiredMark={false}>
            <Form.Item 
              label={<Text strong><InfoCircleOutlined style={{ color: '#FFCC29' }} /> {t('supportChat.typeLabel')}</Text>} 
              name="type" 
              rules={[{ required: true, message: t('supportChat.typeRequired') }]}
            >
              <Select 
                placeholder={t('supportChat.typePlaceholder')}
                style={{ height: '45px' }}
                options={ticketTypeOptions}
              />
            </Form.Item>

            <Form.Item 
              label={<Text strong><QuestionCircleOutlined style={{ color: '#FFCC29' }} /> {t('supportChat.messageLabel')}</Text>} 
              name="description" 
              rules={[
                { required: true, message: t('supportChat.messageRequired') },
                { min: 10, message: t('supportChat.messageMin') }
              ]}
            >
              <TextArea 
                rows={5} 
                placeholder={t('supportChat.messagePlaceholder')} 
                style={{ borderRadius: '12px', padding: '12px' }}
                showCount 
                maxLength={500} 
              />
            </Form.Item>

            <div style={{ marginTop: '30px' }}>
              <Button 
                type="primary" 
                htmlType="submit" 
                block 
                loading={loading}
                icon={<SendOutlined />}
                style={{ 
                  height: '50px', 
                  borderRadius: '15px', 
                  fontWeight: 700,
                  fontSize: '15px',
                  backgroundColor: '#FFCC29',
                  borderColor: '#FFCC29',
                  color: '#000',
                  boxShadow: '0 4px 10px rgba(255, 204, 41, 0.3)'
                }}
              >
                {t('supportChat.submit')}
              </Button>
              <Button 
                type="text" 
                block 
                onClick={() => setIsModalOpen(false)}
                style={{ marginTop: '8px', color: '#8c8c8c' }}
              >
                {t('supportChat.cancel')}
              </Button>
            </div>
          </Form>
        </Modal>
      </>
    </ConfigProvider>
  );
};

export default SupportChat;
